'use client'

// Two-variant picker (mobile + desktop). Each variant hardcodes its own
// layout constants (item width, visible count, center offset, padding) and is
// toggled via Tailwind's `md:hidden` / `hidden md:block` so the browser picks
// the right one at first paint based on viewport.
//
// Why this is structured this way:
// The previous implementation used `useBreakpoint()` to drive layout-affecting
// values (the inline `dimensions` style on the picker container, each child
// AppIcon's `size` prop, `visibleItems`, etc.). `useBreakpoint` returns
// `md = false` during SSR — the server has no idea what viewport you're on —
// and flips to the real value on the client at hydration. On a desktop
// browser that flip caused the picker to grow from mobile dimensions to
// desktop dimensions after first paint, which reflowed the centered hero
// block and produced a 0.147 desktop CLS score on `/alternatives/[slug]`.
//
// CSS-driven sizing fixes this: the browser applies the correct breakpoint
// before paint, so there's no hydration shift. Hardcoded constants per
// variant (rather than CSS custom properties for both dimensions) was chosen
// to avoid introducing a new pattern to the codebase — the two-variants
// approach is already used in `AlternativeHero.tsx` for the head icon.
//
// See `plan/cls/alternatives-usebreakpoint.md` (root-cause analysis) and
// `plan/cls/alternatives-apppicker-refactor.md` (this implementation plan).

import { useEffect, useRef } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { cn, getBrandColors } from '@/lib/utils'
import type { App } from '@/payload-types'

type AppPickerProps = {
  apps: App[]
  currentIndex: number
  setCurrentIndexAction: (index: number) => void
  direction?: 'horizontal' | 'vertical'
  children?: React.ReactNode
}

// Layout constants for each viewport. Each variant uses exactly one set; no
// JS-conditional switching at runtime → no hydration reflow.
const GAP = 16
const PADDING = 24
const MOBILE = {
  itemWidth: 44,
  horizontalVisible: 4,
  verticalVisible: 6,
  centerOffset: 2,
  horizontalPadding: 12,
  verticalPadding: 24,
  // Tailwind class for the child AppIcon wrapper. Hardcoded (not interpolated)
  // so Tailwind's JIT can detect it at build time. `size-11` = 44px.
  iconClass: 'size-11',
  // Prev/next button sizing — h-10 w-10 = 40px (the mobile touch target).
  buttonClass: 'h-10 w-10',
} as const
const DESKTOP = {
  itemWidth: 56,
  horizontalVisible: 6,
  verticalVisible: 5,
  centerOffset: 3,
  horizontalPadding: 24,
  verticalPadding: 48,
  // `size-14` = 56px.
  iconClass: 'size-14',
  // Desktop buttons are smaller (h-6 w-6 = 24px) since pointer precision is better.
  buttonClass: 'h-6 w-6',
} as const

type LayoutConstants = typeof MOBILE | typeof DESKTOP

export default function AppPicker(props: AppPickerProps) {
  return (
    // Outer wrapper preserves the original flex layout (children + picker side
    // by side). `children` is rendered exactly once — outside the variant
    // toggle — to avoid duplicating its DOM. Consumers already pass children
    // with their own `hidden md:block` / `md:hidden` visibility classes
    // (e.g. AlternativeHero's head icon and progress indicator).
    <div className="flex items-center gap-4">
      {props.children}

      {/* Mobile-only render. `md:hidden` resolves at paint time, so the
          desktop browser never renders this variant's box model. */}
      <div className="md:hidden">
        <PickerVariant {...props} layout={MOBILE} />
      </div>

      {/* Desktop-only render. Mirrors the mobile variant with desktop
          constants. Both variants share the same `currentIndex` from props,
          so they stay in sync — only one is visible at a time. */}
      <div className="hidden md:block">
        <PickerVariant {...props} layout={DESKTOP} />
      </div>
    </div>
  )
}

// Inner picker — same logic as before, but with all viewport-dependent
// values passed in via the `layout` prop instead of derived from
// `useBreakpoint()`. No hook calls that depend on viewport, so SSR and
// hydration produce identical DOM (only `display: none` differs, which is
// CSS not JS, so it applies before paint).
type PickerVariantProps = AppPickerProps & { layout: LayoutConstants }

function PickerVariant({
  apps,
  currentIndex,
  setCurrentIndexAction,
  direction = 'horizontal',
  layout,
}: PickerVariantProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])

  const isHorizontal = direction === 'horizontal'
  const isVertical = direction === 'vertical'

  const ITEM_WIDTH = layout.itemWidth
  const visibleItems = isVertical ? layout.verticalVisible : layout.horizontalVisible
  const centerOffset = layout.centerOffset
  const sidePadding = isHorizontal ? layout.horizontalPadding : layout.verticalPadding

  const scrollable = apps.length > visibleItems

  function getScrollOffset(index: number) {
    const maxTranslate = Math.max(0, apps.length - visibleItems)
    return Math.max(0, Math.min(index - centerOffset, maxTranslate))
  }

  function translate(index: number) {
    return -(ITEM_WIDTH + GAP) * getScrollOffset(index)
  }

  // Ensure itemRefs array has the correct length
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, apps.length)
    while (itemRefs.current.length < apps.length) {
      itemRefs.current.push(null)
    }
  }, [apps.length])

  const isAtStart = currentIndex === 0
  const isAtEnd = currentIndex === apps.length - 1

  const scrollOffset = getScrollOffset(currentIndex)
  const maxScrollOffset = Math.max(0, apps.length - visibleItems)
  const showPrevGradient = scrollOffset > 0
  const showNextGradient = scrollOffset !== maxScrollOffset

  function handlePrevious() {
    if (isAtStart) return
    setCurrentIndexAction(currentIndex - 1)
  }

  function handleNext() {
    if (isAtEnd) {
      setCurrentIndexAction(0)
      return
    }
    setCurrentIndexAction(currentIndex + 1)
  }

  const dir = isVertical ? 'to bottom' : 'to right'
  const mask = scrollable
    ? showPrevGradient && showNextGradient
      ? `linear-gradient(${dir}, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 25%, rgba(255,255,255,1) 75%, rgba(255,255,255,0) 100%)`
      : showPrevGradient
        ? `linear-gradient(${dir}, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 25%, rgba(255,255,255,1) 75%, rgba(255,255,255,1) 100%)`
        : showNextGradient
          ? `linear-gradient(${dir}, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 25%, rgba(255,255,255,1) 75%, rgba(255,255,255,0) 100%)`
          : `linear-gradient(${dir}, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 25%, rgba(255,255,255,1) 75%, rgba(255,255,255,1) 100%)`
    : `linear-gradient(${dir}, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 25%, rgba(255,255,255,1) 75%, rgba(255,255,255,1) 100%)`

  const totalVisible = Math.min(apps.length, visibleItems)
  const gap = (totalVisible - 1) * GAP + PADDING

  // Container dimensions are now computed from a single set of constants per
  // variant. The same JS runs on server and client → no hydration mismatch.
  const dimensions = {
    width: isHorizontal ? totalVisible * ITEM_WIDTH + gap : ITEM_WIDTH + sidePadding,
    height: isVertical ? totalVisible * ITEM_WIDTH + gap : ITEM_WIDTH + sidePadding,
  }

  return (
    <div
      className={cn('relative z-20 flex items-center', {
        'absolute left-0 top-1/2 -translate-y-1/2 flex-col': isVertical,
      })}
    >
      {scrollable && (
        <button
          onClick={handlePrevious}
          className={cn(
            'group z-10 flex items-center justify-center rounded-full bg-transparent transition-all hover:bg-white/10',
            layout.buttonClass,
            isAtStart && 'pointer-events-none opacity-50',
            isVertical && 'rotate-90 md:mb-2.5',
            isHorizontal && 'md:mr-2.5',
          )}
        >
          <Icon name="chevron-left" size={12} />
        </button>
      )}
      <div
        style={{ mask, transition: 'mask 300ms ease-in-out', ...dimensions }}
        className="relative"
      >
        <div
          ref={scrollContainerRef}
          className={cn('flex items-center gap-4 transition-transform duration-300 ease-in-out', {
            'w-full flex-col py-3': isVertical,
            'h-full px-3': isHorizontal,
          })}
          style={{
            transform: isVertical
              ? `translateY(${translate(currentIndex)}px)`
              : `translateX(${translate(currentIndex)}px)`,
          }}
        >
          {apps.map((app, index) => {
            const { backgroundColor } = getBrandColors(app)

            return (
              <div
                key={`app-${app.id}-${index}`}
                ref={(el) => {
                  itemRefs.current[index] = el
                }}
                // CSS-driven icon size. Drops the previous `size={isMobile ? 44 : 56}`
                // prop on AppIcon (which wrote width/height inline at hydration and
                // caused the flip). With no `size` prop, AppIcon fills its wrapper
                // at 100% → the wrapper's `size-11`/`size-14` class controls the
                // rendered dimensions and is resolved at first paint.
                className={cn('shrink-0', layout.iconClass)}
              >
                <AppIcon
                  onActivate={() => setCurrentIndexAction(index)}
                  icon={app.icon}
                  glyph={app.glyph}
                  name={app.name}
                  background={backgroundColor}
                  active={currentIndex === index}
                  invert={true}
                />
              </div>
            )
          })}
        </div>
      </div>

      {scrollable && (
        <button
          onClick={handleNext}
          className={cn(
            'group z-10 flex items-center justify-center rounded-full bg-transparent transition-colors hover:bg-white/10',
            layout.buttonClass,
            isVertical && 'rotate-90 md:mt-2.5',
            isHorizontal && 'md:ml-2.5',
          )}
        >
          <Icon name="chevron-left" size={12} className="rotate-180" />
        </button>
      )}
    </div>
  )
}
